import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {

    static targets = ["modal", "searchInput", "results", "unlinkBtn", "searchBtn", "lockIcon", "preview"]

    static values = {
        searchUrl: String,
        linkUrl:   String,
        unlinkUrl: String,
        photoUrl:  String,
        synced:    String,
    }

    declare readonly modalTarget:     HTMLElement
    declare readonly searchInputTarget: HTMLInputElement
    declare readonly resultsTarget:   HTMLElement
    declare readonly hasUnlinkBtnTarget: boolean
    declare readonly unlinkBtnTarget: HTMLElement
    declare readonly hasSearchBtnTarget: boolean
    declare readonly searchBtnTarget: HTMLElement
    declare readonly hasLockIconTarget: boolean
    declare readonly lockIconTarget:  HTMLElement
    declare readonly hasPreviewTarget: boolean
    declare readonly previewTarget: HTMLElement

    declare searchUrlValue: string
    declare linkUrlValue:   string
    declare unlinkUrlValue: string
    declare photoUrlValue:  string
    declare syncedValue:    string

    private modalInstance: Modal | null = null
    private debounceTimer: ReturnType<typeof setTimeout> | null = null

    /* ==============================
     * MODAL
     * ============================== */

    open(): void {
        if (!this.modalInstance) {
            this.modalInstance = new Modal(this.modalTarget)
        }
        this.resultsTarget.innerHTML =
            '<tr><td colspan="6" class="text-muted text-center py-3">Escribe para buscar...</td></tr>'
        this.searchInputTarget.value = ''
        if (this.hasPreviewTarget) this.previewTarget.classList.add('d-none')
        this.modalInstance.show()
        setTimeout(() => this.searchInputTarget.focus(), 300)
    }

    /* ==============================
     * SEARCH (debounced)
     * ============================== */

    search(): void {
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => this.doSearch(), 300)
    }

    private async doSearch(): Promise<void> {
        const q = this.searchInputTarget.value.trim()
        if (q.length < 2) {
            this.resultsTarget.innerHTML =
                '<tr><td colspan="6" class="text-muted text-center py-3">Escribe al menos 2 caracteres...</td></tr>'
            return
        }

        this.resultsTarget.innerHTML =
            '<tr><td colspan="6" class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>'

        try {
            const res  = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`)
            const data = await res.json()

            if (!Array.isArray(data) || data.length === 0) {
                this.resultsTarget.innerHTML =
                    '<tr><td colspan="6" class="text-muted text-center py-3">Sin resultados.</td></tr>'
                return
            }

            this.resultsTarget.innerHTML = data.map((row: any) => `
                <tr>
                    <td>${this.esc(row.name ?? '')}</td>
                    <td><small class="text-muted">${this.esc(row.email ?? '')}</small></td>
                    <td><small>${this.esc(row.phone ?? '')}</small></td>
                    <td><small>${this.esc(row.ordem ?? '')}</small></td>
                    <td><small>${this.esc(row.status ?? '')}</small></td>
                    <td class="text-end">
                        <button class="btn btn-primary btn-sm"
                                data-action="click->congregatio#use"
                                data-id="${row.id}"
                                data-name="${this.esc(row.name ?? '')}"
                                data-email="${this.esc(row.email ?? '')}"
                                data-phone="${this.esc(row.phone ?? '')}">
                            Usar
                        </button>
                    </td>
                </tr>`
            ).join('')
        } catch {
            this.resultsTarget.innerHTML =
                '<tr><td colspan="6" class="text-danger text-center py-3">Error al conectar con Congregatio.</td></tr>'
        }
    }

    /* ==============================
     * USE — muestra preview para que el usuario decida
     * ============================== */

    async use(event: Event): Promise<void> {
        const btn  = (event.currentTarget as HTMLElement)
        const id   = parseInt(btn.dataset.id ?? '0', 10)
        const name = btn.dataset.name ?? ''
        if (!id) return

        btn.setAttribute('disabled', 'true')
        btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span>'

        try {
            // Fetch foto (solo al pulsar "Usar")
            const photoRes  = await fetch(this.photoUrlValue.replace('__ID__', String(id)))
            const photoData = await photoRes.json()

            this.showPreview(id, btn.dataset, photoData.photo)
        } catch {
            alert('Error inesperado al cargar los datos.')
        } finally {
            btn.removeAttribute('disabled')
            btn.innerHTML = 'Usar'
        }
    }

    private showPreview(id: number, data: DOMStringMap, photo: string | null): void {
        if (!this.hasPreviewTarget) return

        const img = photo
            ? `<img src="${photo}" class="img-thumbnail me-3" style="width:80px;height:80px;object-fit:cover;">`
            : `<div class="bg-secondary rounded me-3 d-flex align-items-center justify-content-center" style="width:80px;height:80px;flex-shrink:0;">
                   <i class="bi bi-person-fill text-white fs-3"></i>
               </div>`

        this.previewTarget.innerHTML = `
            <div class="border rounded p-3 bg-light">
                <p class="fw-semibold mb-2">¿Vincular con este miembro?</p>
                <div class="d-flex align-items-center">
                    ${img}
                    <div>
                        <div class="fw-semibold">${this.esc(data.name ?? '')}</div>
                        <div class="text-muted small">${this.esc(data.email ?? '')}</div>
                        <div class="text-muted small">${this.esc(data.phone ?? '')}</div>
                    </div>
                </div>
                <div class="d-flex gap-2 mt-3">
                    <button class="btn btn-primary btn-sm"
                            data-action="click->congregatio#confirm"
                            data-id="${id}"
                            data-photo="${this.esc(photo ?? '')}">
                        <i class="bi bi-check-circle me-1"></i>Confirmar y vincular
                    </button>
                    <button class="btn btn-outline-secondary btn-sm"
                            data-action="click->congregatio#cancelPreview">
                        Cancelar
                    </button>
                </div>
            </div>`

        this.previewTarget.classList.remove('d-none')
        this.previewTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

    cancelPreview(): void {
        if (this.hasPreviewTarget) this.previewTarget.classList.add('d-none')
    }

    /* ==============================
     * CONFIRM — vincula efectivamente
     * ============================== */

    async confirm(event: Event): Promise<void> {
        const btn   = (event.currentTarget as HTMLElement)
        const id    = parseInt(btn.dataset.id ?? '0', 10)
        const photo = btn.dataset.photo ?? null
        if (!id) return

        btn.setAttribute('disabled', 'true')
        btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Vinculando...'

        try {
            const linkRes  = await fetch(this.linkUrlValue, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ congregatioId: id }),
            })
            const linkData = await linkRes.json()

            if (!linkRes.ok) {
                alert(linkData.error ?? 'Error al vincular.')
                btn.removeAttribute('disabled')
                btn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Confirmar y vincular'
                return
            }

            this.fillForm(linkData, photo || null)
            this.modalInstance?.hide()

        } catch {
            alert('Error inesperado al vincular.')
            btn.removeAttribute('disabled')
            btn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Confirmar y vincular'
        }
    }

    private fillForm(data: any, photo: string | null): void {
        const form = this.element.querySelector<HTMLFormElement>('#user-edit-form')
        if (!form) return

        // Rellenar campos de texto
        const setField = (name: string, value: string) => {
            const input = form.querySelector<HTMLInputElement>(`[name$="[${name}]"]`)
                       ?? form.querySelector<HTMLInputElement>(`[name="${name}"]`)
            if (input) input.value = value
        }

        setField('name',     data.name  ?? '')
        setField('username', data.email ?? '')
        setField('phone',    data.phone ?? '')

        // Actualizar preview de imagen
        const preview = this.element.querySelector<HTMLImageElement>('#preview-image')
        if (preview && photo) {
            preview.src = photo
        } else if (preview && data.image) {
            preview.src = data.image
        }

        // Actualizar indicador de sync en el header
        this.updateSyncIndicator(data)
    }

    private updateSyncIndicator(data: any): void {
        // Ocultar botón buscar, mostrar estado vinculado
        if (this.hasSearchBtnTarget) {
            this.searchBtnTarget.classList.add('d-none')
        }
        if (this.hasLockIconTarget) {
            this.lockIconTarget.className = 'bi bi-lock-fill text-danger'
        }
    }

    /* ==============================
     * UNLINK (desvincular)
     * ============================== */

    async unlink(): Promise<void> {
        if (!confirm('¿Desvincular este usuario de Congregatio? Los campos volverán a ser editables.')) return

        try {
            const res = await fetch(this.unlinkUrlValue, { method: 'POST' })
            if (res.ok) {
                window.location.reload()
            }
        } catch {
            alert('Error al desvincular.')
        }
    }

    /* ==============================
     * HELPERS
     * ============================== */

    private esc(str: string): string {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
    }
}
